import { useEffect, useMemo, useState } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import { invalidateFollowersRolesEventListener } from '@space-lib-events/invalidateFollowersRolesQuery'
import Loader from '@space-lib/components/loader'
import Table from '@space-lib/components/table'
import { SortMethodTypes, sorter } from '@space-lib/utils/sorter'
import type { CommunityMember } from '@space-shell/common/services/communities'
import { CommunitiesEndpoints } from '@space-shell/common/services/communities'
import { queryClient } from '@space-shell/config/request'
import { COMMUNITY_TYPE } from '@space-shell/modules/communities/accessAndPrivacy/constants'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useGetFollowersRoles } from '@space-shell/modules/communities/hooks/useGetFollowersRoles'

import {
  MemberListRow,
  MemberListHeader,
  ModerationListEmptyState
} from '@space-shell/modules/communities/membersAndPermissions/components'
import { useTranslation } from 'react-i18next'

import type { ModerationTableProps } from './types'

import { MIN_QTD_TO_SORT } from '../../constants'
import type { OrderFields } from '../../types'

const COL_SPAN_4 = 4
const COL_SPAN_3 = 3
const COL_SPAN_2 = 2

const ModerationTable = ({ memberType, query }: ModerationTableProps) => {
  const [{ enabled: isRemoveActionEnabled }] = useDecision(
    'com_adm_web_enable_new_members_list_remove'
  )
  const [{ enabled: isExpirationDateActionEnabled }] = useDecision(
    'com_adm_web_enable_new_members_list_expiration_date'
  )
  const { community } = useCommunityConfigurationSidebar()

  const shouldRenderExpirationDate =
    isExpirationDateActionEnabled && community.type === COMMUNITY_TYPE.EXCLUSIVE

  const getColSpan = () => {
    if (isRemoveActionEnabled && isExpirationDateActionEnabled) {
      return COL_SPAN_4
    } else if (isRemoveActionEnabled || isExpirationDateActionEnabled) {
      return COL_SPAN_3
    }

    return COL_SPAN_2
  }

  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isRefetching,
    refetch: refetchGetFollowersRoles
  } = useGetFollowersRoles(memberType, community.id, query)
  const { t } = useTranslation()
  const [sortMeta, setSortMeta] = useState<{
    sortKey: keyof CommunityMember
    orderType: SortMethodTypes
  }>({
    orderType: SortMethodTypes.DESC,
    sortKey: 'id'
  })

  const members = useMemo(() => {
    const _members =
      data?.pages?.reduce((pages, page) => pages.concat(page.data), [] as CommunityMember[]) ?? []

    return [..._members].sort(sorter<CommunityMember>(sortMeta.orderType, sortMeta.sortKey))
  }, [data, sortMeta])

  const handleOrder = (sortKey: OrderFields) => {
    setSortMeta((prev) => ({
      orderType:
        prev.orderType === SortMethodTypes.ASC ? SortMethodTypes.DESC : SortMethodTypes.ASC,
      sortKey
    }))
  }

  const handleLoadMore = () => {
    fetchNextPage()
  }

  useEffect(() => {
    invalidateFollowersRolesEventListener(() => {
      queryClient.invalidateQueries([CommunitiesEndpoints.getFollowersRoles(Number(community.id))])
    })
  }, [community.id])

  if (isLoading || isRefetching) {
    return <Loader full />
  }

  return (
    <>
      <Table className='hot-table--borderless'>
        <MemberListHeader
          onClick={handleOrder}
          isRemoveActionEnabled={isRemoveActionEnabled}
          shouldRenderExpirationDate={shouldRenderExpirationDate}
          isActiveOrder={{
            memberType:
              sortMeta.sortKey === 'memberType' && sortMeta.orderType === SortMethodTypes.DESC,
            name: sortMeta.sortKey === 'name' && sortMeta.orderType === SortMethodTypes.DESC
          }}
          disabled={members?.length < MIN_QTD_TO_SORT}
        />
        <Table.Body>
          {members?.length > 0 ? (
            members.map((member) => (
              <MemberListRow
                key={String(member.id)}
                member={member}
                communityId={community.id}
                isRemoveActionEnabled={isRemoveActionEnabled}
                shouldRenderExpirationDate={shouldRenderExpirationDate}
                refetchGetFollowersRoles={refetchGetFollowersRoles}
              />
            ))
          ) : (
            <Table.Row>
              <Table.Data colSpan={getColSpan()}>
                <ModerationListEmptyState hide={isFetching} isError={isError} />
              </Table.Data>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      {!isFetching && hasNextPage && (
        <div className='_d-flex _justify-content-center _py-6'>
          <button
            className='hot-button hot-button--primary'
            type='button'
            disabled={!hasNextPage}
            onClick={handleLoadMore}>
            {t('buttons.show_more')}
          </button>
        </div>
      )}

      {isFetchingNextPage && <Loader full />}
    </>
  )
}

export default ModerationTable
